/**
 * HTTP layer for group data imports — outfitting a group by uploading a
 * document of structures and their panels.
 *
 * The flow is two calls: a preflight that answers what the document would
 * produce without writing anything, then a submit that hands the writes to a
 * worker and answers immediately, because a few hundred structures is
 * thousands of writes and no request survives them. The submitter is emailed
 * when the worker finishes.
 *
 * Contains:
 * - preflightGroupImport: write-nothing preview of an uploaded document
 * - submitGroupImport: accept a document and queue the import
 * - fetchImportFieldCatalog: the fields a document's columns can map to
 */
import apiClient from '@sophocles/http-common';

const DATA_IMPORTS_ENDPOINT = '/odl-app/data-imports';

/**
 * Read a document and report what importing it would produce.
 *
 * Nothing is written. The body carries the resolved column mapping, the
 * columns nothing claimed, every warning the read collected, and the counts
 * for the whole document.
 *
 * An RPC read: the response body is the preview itself, so `res.data` is the
 * payload — there is no `{data: …}` envelope to unwrap.
 *
 * @param {FormData} payload - multipart body carrying `file`, `entity_id` and
 *   an optional `field_map`. Axios sets the multipart boundary itself.
 * @returns {Promise<{field_map: object, unmapped_columns: string[],
 *   missing_required: string[], ambiguous: string[],
 *   warnings: {row: ?number, column: ?string, field: ?string, code: string, message: string}[],
 *   summary: object, sites: object[]}>}
 */
export const preflightGroupImport = payload =>
  apiClient.post(`${DATA_IMPORTS_ENDPOINT}/preflight/`, payload).then(res => res.data);

/**
 * Accept a document and queue the import.
 *
 * Answers 202 as soon as the document reads cleanly; the structures and
 * panels are written by a worker afterwards.
 *
 * An Archetype-A CRUD write: the body is the `{data: row}` envelope, so the
 * import row is at `res.data.data`. There is deliberately no `?? res.data`
 * fallback — a bare row would mean the endpoint stopped honouring the
 * envelope, and failing loudly beats resolving a row-shaped `undefined`.
 *
 * @param {FormData} payload - the same multipart body the preflight accepts.
 * @returns {Promise<object>} The queued import as the history grid renders it.
 */
export const submitGroupImport = payload =>
  apiClient.post(`${DATA_IMPORTS_ENDPOINT}/`, payload).then(res => res.data.data);

/**
 * List the fields a document's columns can be mapped to.
 *
 * An RPC read whose body is `{fields: [...]}`; the array is the payload.
 *
 * @returns {Promise<{name: string, label: string, scope: string,
 *   required: boolean, aliases: string[], max_length: ?number}[]>}
 */
export const fetchImportFieldCatalog = () =>
  apiClient.get(`${DATA_IMPORTS_ENDPOINT}/field-catalog/`).then(res => res.data.fields);
